import { laravelExpertiseData } from "@/mocks/homeData";
import AnimatedSection from "@/components/AnimatedSection";

const techNodes = [
  { label: "Laravel", x: 50, y: 15, color: "accent" },
  { label: "PHP", x: 20, y: 35, color: "secondary" },
  { label: "REST API", x: 80, y: 30, color: "accent" },
  { label: "AI", x: 40, y: 25, color: "accent" },
  { label: "Redis", x: 15, y: 65, color: "secondary" },
  { label: "MySQL", x: 50, y: 50, color: "accent" },
  { label: "AWS", x: 85, y: 60, color: "secondary" },
  { label: "Queue", x: 30, y: 80, color: "secondary" },
  { label: "Cloudflare", x: 70, y: 85, color: "accent" },
];

function ArchitectureVisual() {
  return (
    <div className="relative w-full aspect-square max-w-[420px] mx-auto">
      {/* Central hub */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-2xl bg-accent-500/10 border-2 border-accent-500/30 flex items-center justify-center z-10 animate-pulse-glow">
        <span className="text-accent-600 font-bold text-sm font-heading">Laravel</span>
      </div>

      {/* Connection lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
        {techNodes.map((node, i) => (
          <line
            key={i}
            x1="50"
            y1="50"
            x2={node.x}
            y2={node.y}
            stroke="oklch(var(--accent-500) / 0.2)"
            strokeWidth="0.3"
            strokeDasharray="2 2"
          >
            <animate
              attributeName="stroke-dashoffset"
              from="0"
              to="4"
              dur="2s"
              repeatCount="indefinite"
            />
          </line>
        ))}
      </svg>

      {/* Tech nodes */}
      {techNodes.map((node, i) => (
        <div
          key={node.label}
          className={`absolute px-3 py-1.5 rounded-lg text-xs font-semibold backdrop-blur-sm border animate-float-slow ${
            node.color === "accent"
              ? "bg-accent-500/10 border-accent-500/20 text-accent-700"
              : "bg-secondary-500/10 border-secondary-500/20 text-secondary-700"
          }`}
          style={{
            left: `${node.x}%`,
            top: `${node.y}%`,
            transform: "translate(-50%, -50%)",
            animationDelay: `${i * 0.3}s`,
          }}
        >
          {node.label}
        </div>
      ))}

      {/* Decorative rings */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[70%] rounded-full border border-accent-500/10" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[55%] h-[55%] rounded-full border border-primary-300/20" />
    </div>
  );
}

export default function LaravelExpertiseSection() {
  return (
    <section className="py-20 md:py-28 bg-laravel-section relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary-500/5 rounded-full blur-3xl" />
      <div className="absolute inset-0 dot-pattern opacity-30" />

      <div className="container-astro relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-16 items-center">
          <div>
            <AnimatedSection>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-accent-500/10 border border-accent-500/20 mb-6">
                <span className="text-xs font-semibold text-accent-600 tracking-wide uppercase">
                  Laravel Specialists
                </span>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={100}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-6 font-heading tracking-tight leading-tight">
                {laravelExpertiseData.heading}
              </h2>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8 max-w-lg">
                {laravelExpertiseData.desc}
              </p>
            </AnimatedSection>

            <AnimatedSection delay={300}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 mb-10">
                {laravelExpertiseData.items.map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-2.5 text-sm text-foreground-700"
                  >
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-500/15 text-accent-600 shrink-0">
                      <i className="ri-check-line text-xs" />
                    </span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            <AnimatedSection delay={400}>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold rounded-xl hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-[#7C3AED]/25 whitespace-nowrap btn-arrow-hover group"
              >
                {laravelExpertiseData.cta}
                <i className="ri-arrow-right-line btn-arrow transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </AnimatedSection>
          </div>

          <AnimatedSection delay={200} direction="left">
            <ArchitectureVisual />
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}